import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { FaqJsonLd } from '../components/FaqJsonLd';
import {
  CheckCircle,
  Sparkle,
  FlowerTulip,
  Drop,
  Calculator,
  CloudSun,
  ArrowRight,
  Handshake,
} from '@phosphor-icons/react';

/**
 * /compare/sunday-vs-turfpulse — positive-framing comparison page.
 *
 * Per /app/docs/seo-wip-policy.md:
 *   • Shared strengths first
 *   • Honest "when to choose them" section (no bashing)
 *   • Differentiators framed factually, not as attacks
 *   • Use-together section where the story is honest
 *
 * Launched as WIP (noindex) until copy is approved by the owner.
 */

const sharedStrengths = [
  'Rooted in soil-test-driven recommendations',
  'Designed for homeowners who want better results without going pro',
  'Remove guesswork around timing, products, and rates',
  'Clear, non-jargon guidance — no pesticide applicator license required',
];

const whenSundayFits = [
  {
    icon: <FlowerTulip size={22} weight="duotone" />,
    title: 'You want the products shipped to you',
    body: 'Sunday mails you seasonal nutrient pouches matched to your soil test. If you do not want to source, measure, and stage product yourself, that bundled convenience is real.',
  },
  {
    icon: <Sparkle size={22} weight="duotone" />,
    title: 'You prefer bio-based, low-synthetic nutrients',
    body: 'Sunday\'s nutrient packs lean on bio-based inputs like seaweed and molasses. If "as natural as practical" is a priority for you, that is part of their core identity.',
  },
  {
    icon: <Handshake size={22} weight="duotone" />,
    title: 'You want a single-decision subscription',
    body: 'Pay once, receive everything, follow the pouch instructions. For year-one homeowners who want low cognitive load, that is a legitimate strength.',
  },
];

const whenTurfPulseFits = [
  {
    icon: <Calculator size={22} weight="duotone" />,
    title: 'You want to know the "why" and the "when"',
    body: 'TurfPulse computes Growing Degree Days (GDD) from your local weather and tells you the window to apply PGR, pre-emergents, or fungicides. It is a math layer on top of whatever products you choose.',
  },
  {
    icon: <Drop size={22} weight="duotone" />,
    title: 'You want to dial in irrigation',
    body: 'Water Budget calculator, catch-cup tool, and optional Ecowitt soil-moisture integration close the loop between weather forecast and how long your zones should run. Works alongside Rachio if you have one.',
  },
  {
    icon: <CloudSun size={22} weight="duotone" />,
    title: 'You already have products or want flexibility',
    body: 'TurfPulse does not sell or ship product. Bring whatever you want — store-brand urea, a pro-grade PGR from your favorite supplier, or even Sunday pouches — and TurfPulse will help you time the application.',
  },
];

// Pre-wrapped Awin deep link for DoMyOwn Lawn Box subscription.
// awinmid=88419 (DoMyOwn) · awinaffid=2806920 (Hi Tex Lawn Pro LLC)
const LAWN_BOX_AWIN_URL =
  'https://www.awin1.com/cread.php?awinmid=88419&awinaffid=2806920&ued=' +
  encodeURIComponent('https://www.domyown.com/subscriptions/lawn-box-program');

const useTogether = [
  'Use Sunday for your seasonal nutrient rotation — let the pouches handle macros.',
  'Use TurfPulse to time your weed pre-emergent window, PGR applications, and irrigation.',
  'Log every Sunday application in TurfPulse so you have a full-season record and can spot what worked.',
  <>
    Same logic applies whether you go with Sunday,{' '}
    <a
      href={LAWN_BOX_AWIN_URL}
      target="_blank"
      rel="sponsored noopener noreferrer"
      className="text-primary underline hover:no-underline font-semibold"
      data-testid="domyown-lawnbox-affiliate-link"
    >
      DoMyOwn's Lawn Box
    </a>
    , Lawnbright, or any other subscription — TurfPulse times whatever shows up at your door.
  </>,
];

const faqs = [
  {
    q: 'Is TurfPulse a replacement for Sunday Lawn Care?',
    a: 'They solve different problems. Sunday is a product subscription; TurfPulse is an intelligence layer. Many enthusiasts use both — Sunday for the nutrients, TurfPulse for the timing and irrigation math.',
  },
  {
    q: 'Does TurfPulse sell fertilizer or lawn products?',
    a: 'No. TurfPulse is software. When we recommend products we link to Amazon via affiliate links, but we never handle or ship physical goods. You source whatever you want.',
  },
  {
    q: 'Do I need a soil test to use TurfPulse?',
    a: 'Not required, but recommended. Upload a standard soil-lab PDF to /soil-test and we parse the macros and micros automatically. Without one, you can still use GDD tracking, Water Budget, disease models, and the catch-cup tool.',
  },
  {
    q: 'Which one is cheaper?',
    a: 'Sunday is a seasonal product spend (varies by lawn size). TurfPulse has a free tier and paid tiers for advanced features. If you already buy your own products, TurfPulse is typically a small add-on cost.',
  },
];

const SundayComparePage = () => {
  return (
    <div data-testid="sunday-compare-page" className="min-h-screen bg-background">
      <SEO
        title="Sunday Lawn Care vs TurfPulse — Honest Comparison"
        description="An even-handed look at Sunday Lawn Care and TurfPulse for homeowners — shared strengths, where each one fits best, and how to use them together."
        path="/compare/sunday-vs-turfpulse"
      />
      <BreadcrumbJsonLd
        items={[
          { label: 'TurfPulse', path: '/' },
          { label: 'Compare', path: '/compare' },
          { label: 'Sunday vs TurfPulse', path: '/compare/sunday-vs-turfpulse' },
        ]}
      />
      <FaqJsonLd items={faqs} />

      {/* Hero */}
      <section className="px-4 sm:px-6 pt-12 pb-10 border-b border-border/60">
        <div className="max-w-4xl mx-auto space-y-5">
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
            Honest Comparison
          </p>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight text-foreground leading-[1.1]">
            Sunday Lawn Care vs. TurfPulse
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl leading-relaxed">
            Two tools that can both make your lawn better — designed around very different
            philosophies. Here's an even-handed look so you can pick the right one (or use both).
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            <Button asChild size="lg" data-testid="hero-cta-hey">
              <Link to="/hey">See what TurfPulse does in your ZIP <ArrowRight className="ml-2" size={18} /></Link>
            </Button>
            <Button asChild variant="outline" size="lg" data-testid="hero-cta-kit">
              <Link to="/kit">Browse the starter kit</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Shared Strengths */}
      <section className="px-4 sm:px-6 py-14 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
            What they get right — both of them
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl">
            Before the differences, it's worth naming the common ground. If you're comparing these
            two at all, you're already past the "just throw down Scotts" phase.
          </p>
          <ul className="grid sm:grid-cols-2 gap-4">
            {sharedStrengths.map((s) => (
              <li
                key={s}
                className="flex gap-3 items-start p-4 rounded-lg border border-border/60 bg-background"
                data-testid="shared-strength"
              >
                <CheckCircle size={22} weight="duotone" className="text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm text-foreground leading-relaxed">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* When Sunday fits */}
      <section className="px-4 sm:px-6 py-14">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
            When Sunday is the right pick
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl">
            Sunday has a clear identity. Here's where that identity genuinely serves you best.
          </p>
          <div className="grid sm:grid-cols-3 gap-5">
            {whenSundayFits.map((item) => (
              <Card key={item.title} data-testid="sunday-fit" className="border-border/60">
                <CardContent className="p-6 space-y-3">
                  <div className="text-primary">{item.icon}</div>
                  <h3 className="font-semibold text-foreground text-base leading-tight">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* When TurfPulse fits */}
      <section className="px-4 sm:px-6 py-14 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
            When TurfPulse is the right pick
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl">
            TurfPulse is built for the homeowner who wants to understand the math, not just follow a
            calendar. Here's where that approach pays off.
          </p>
          <div className="grid sm:grid-cols-3 gap-5">
            {whenTurfPulseFits.map((item) => (
              <Card key={item.title} data-testid="turfpulse-fit" className="border-primary/30 bg-background">
                <CardContent className="p-6 space-y-3">
                  <div className="text-primary">{item.icon}</div>
                  <h3 className="font-semibold text-foreground text-base leading-tight">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Use Together */}
      <section className="px-4 sm:px-6 py-14">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 mb-4">
            <Handshake size={28} weight="duotone" className="text-primary mt-1" />
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              Or use them together
            </h2>
          </div>
          <p className="text-muted-foreground mb-6 max-w-2xl">
            Nothing stops you from combining the two. This is how several of our enthusiasts actually run things:
          </p>
          <Card className="border-primary/40 bg-primary/5">
            <CardContent className="p-6">
              <ul className="space-y-3">
                {useTogether.map((step, i) => (
                  <li key={i} className="flex gap-3 items-start" data-testid="use-together-step">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span className="text-sm text-foreground leading-relaxed pt-0.5">{step}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 sm:px-6 py-14 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-8">Common questions</h2>
          <div className="space-y-5">
            {faqs.map((f) => (
              <div key={f.q} className="border-b border-border/60 pb-5 last:border-0" data-testid="compare-faq">
                <h3 className="font-semibold text-foreground mb-2">{f.q}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 sm:px-6 py-16 border-t border-border/60">
        <div className="max-w-3xl mx-auto text-center space-y-5">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
            See what the math looks like for your lawn
          </h2>
          <p className="text-muted-foreground">
            Enter your ZIP and get today's local GDD, soil-temp estimate, and disease pressure — free, no account.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" data-testid="footer-cta-hey">
              <Link to="/hey">Try it with your ZIP <ArrowRight className="ml-2" size={18} /></Link>
            </Button>
            <Button asChild variant="outline" size="lg" data-testid="footer-cta-water">
              <Link to="/water-budget">Or skip to the Water Budget tool</Link>
            </Button>
          </div>
          <p className="text-xs text-muted-foreground pt-4 max-w-lg mx-auto">
            "Sunday Lawn Care" and related marks are trademarks of their respective owners.
            This page reflects our own experience and opinions and is not affiliated with or endorsed by Sunday.
          </p>
        </div>
      </section>
    </div>
  );
};

export default SundayComparePage;
